import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      errorDetails: null,
      isRetrying: false 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error boundary caught an error:', error, errorInfo);
    this.setState({
      errorDetails: {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      }
    });
  }

  handleRetry = () => {
    this.setState({ isRetrying: true });
    setTimeout(() => {
      this.setState({ 
        hasError: false, 
        errorDetails: null,
        isRetrying: false 
      });
    }, 800);
  };

  render() {
    if (this.state.hasError) {
      return (
    <>    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/3 left-1/3 w-72 h-72 bg-purple-500/30 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/3 right-1/3 w-64 h-64 bg-blue-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>

          {/* Grid pattern */}
          
          <div className="relative flex min-h-screen flex-col items-center justify-center px-6 text-center">
            {/* Icon and title */}
            <div className="mb-8">
              <div className="relative mb-6">
                <AlertTriangle className="w-24 h-24 gradient-text mx-auto animate-pulse" />
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-bounce">
                  <Bug className="w-3 h-3 text-white" />
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-black gradient-text mb-4">
                Component Error
              </h1>
              
              <div className="text-sm font-mono text-gray-400 tracking-widest">
                REACT_ERROR_BOUNDARY_TRIGGERED
              </div>
            </div>

            {/* Main content */}
            <div className="max-w-2xl space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/30 rounded-full">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                    <span className="text-blue-300 text-sm font-medium">
                      🚀 New version deployed
                    </span>
                  </div>
                </div>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  We just launched a fresh update! Sometimes new versions need a moment to settle in. 
                  This component hiccup is likely due to the latest deployment.
                </p>
                
                <p className="text-gray-400">
                  Our developers have been working hard on improvements – try refreshing to see the latest fixes!
                </p>
              </div>

              {/* Error details (collapsed by default) */}
              {/* {this.state.errorDetails && (
                <details className="mt-6 p-4 bg-gray-800/50 border border-gray-700 rounded-lg text-left">
                  <summary className="cursor-pointer text-gray-400 hover:text-gray-300 font-medium mb-2">
                    Technical Details
                  </summary>
                  <div className="text-xs font-mono text-gray-500 space-y-2">
                    <div>
                      <strong className="text-red-400">Error:</strong> {this.state.errorDetails.message}
                    </div>
                    <div className="max-h-32 overflow-y-auto bg-gray-900 p-2 rounded">
                      <strong className="text-yellow-400">Component Stack:</strong>
                      <pre className="whitespace-pre-wrap text-xs mt-1">
                        {this.state.errorDetails.componentStack}
                      </pre>
                    </div>
                  </div>
                </details>
              )} */}

              {/* Status indicator */}
              <div className="flex items-center justify-center gap-3 py-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-full">
                  <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
                  <span className="text-red-300 text-sm font-medium">
                    Component crashed
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
                {/* <button
                  onClick={this.handleRetry}
                  disabled={this.state.isRetrying}
                  className="group relative px-8 py-4 bg-gradient-to-r from-[#B50BDD] to-[#4C0899] hover:from-[#B50BDD]/80 hover:to-[#4C0899]/80 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
                >
                  <div className="flex items-center justify-center gap-3">
                    <RefreshCw className={`w-5 h-5 ${this.state.isRetrying ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                    <span>{this.state.isRetrying ? 'Retrying...' : 'Retry Component'}</span>
                  </div>
                  
                  <div className="absolute inset-0 bg-gradient-to-r from-[#B50BDD] to-[#4C0899] rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300 -z-10"></div>
                </button> */}

                <button
                  onClick={() => window.location.reload()}
                  className="group flex items-center gap-2 px-6 py-4 border-2 border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white font-semibold rounded-xl transition-all duration-300 hover:bg-gray-800/50"
                >
                  <Home className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                  <span>Reload Page</span>
                </button>
              </div>

              {/* Help text */}
              <div className="pt-8 border-t border-gray-800">
                <p className="text-sm text-white">
                  If this error persists, please contact support with the technical details above.
                </p>
              </div>
            </div>

            {/* Floating elements */}
            <div className="absolute bottom-10 left-10 opacity-30">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
            </div>
            <div className="absolute top-20 right-20 opacity-30">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-ping delay-500"></div>
            </div>
          </div>
        </div>
      </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;